"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, CircleCheck as CheckCircle2, Calendar, ArrowRight, ShieldCheck, TrendingUp, UserCheck, Search, Building2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const localPillars = [{
  id: 1,
  icon: MapPin,
  title: "Google Map Pack Dominance",
  desc: "Optimize your Google Business Profile and local geo-signals to secure top-3 local pack rankings across Lincoln."
}, {
  id: 2,
  icon: Search,
  title: "Lincolnshire Geo-Targeting",
  desc: "Capture high-intent local buyers searching for your exact services in Lincoln, Newark, and surrounding areas."
}, {
  id: 3,
  icon: UserCheck,
  title: "1-on-1 Direct Access",
  desc: "You work directly with me—Yassine Essaadi. Zero agency account managers or junior staff in between."
}, {
  id: 4,
  icon: TrendingUp,
  title: "Revenue-Focused Results",
  desc: "Tailored local SEO strategies built to generate qualified calls, inquiries, and trackable business growth."
}];
const trustBadges = ["Direct Consultant Access", "Tailored Lincoln SEO Strategy", "Transparent Monthly Deliverables", "No Hidden Agency Overhead"];
export default function Localhero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localhero" data-nav="dark" className="relative bg-primary text-primary-foreground overflow-hidden pt-12 pb-16 md:pt-20 md:pb-24">
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-accent/15 blur-3xl" />
        <div className="absolute -bottom-40 -left-20 w-80 h-80 rounded-full bg-accent/10 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center" variants={containerVariants} initial="hidden" animate="visible">
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            
            <motion.div variants={itemVariants} className="mb-4">
              <Badge variant="outline" className="bg-accent/20 border-accent/40 text-primary-foreground text-xs md:text-sm font-medium px-3.5 py-1.5 rounded-full flex items-center gap-2 backdrop-blur-sm">
                <MapPin className="size-3.5 text-accent" />
                <span>Lincoln &amp; Lincolnshire Local SEO Specialist</span>
              </Badge>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-[1.15] mb-6">
              Local SEO Consultant in <span className="text-accent underline decoration-accent/40 decoration-wavy underline-offset-8">Lincoln</span>
            </motion.h1>

            <motion.p variants={itemVariants} className="text-base sm:text-lg md:text-xl text-primary-foreground/90 font-default font-normal leading-relaxed mb-8 max-w-2xl">
              I help Lincoln businesses dominate local Google search, map pack rankings, and organic local traffic. When you work with me, you work directly with Yassine Essaadi—no account managers, no generic agency templates, and no junior staff.
            </motion.p>

            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full mb-8">
              {trustBadges.map((badgeText, i) => <div key={i} data-index={i} className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 text-accent shrink-0" />
                  <span className="text-xs sm:text-sm text-primary-foreground/90 font-medium">
                    {badgeText}
                  </span>
                </div>)}
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-medium text-base h-12 px-6 rounded-md gap-2" asChild>
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="size-5" /></SafeHydrate>
                  <span>Book a Free SEO Consultation</span>
                </Link>
              </Button>

              <Button size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground font-medium text-base h-12 px-6 rounded-md gap-2" asChild>
                <Link to="/CaseStudiesPage">
                  <span>Explore My Case Studies</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-6 flex items-center gap-2 text-xs text-primary-foreground/75">
              <ShieldCheck className="size-4 text-accent shrink-0" />
              <span>Independent Expert • Direct Access • Honest Local Strategy</span>
            </motion.div>

          </div>

          <div className="lg:col-span-5 relative">
            <motion.div variants={itemVariants} className="relative mx-auto max-w-md lg:max-w-none">
              
              <div className="relative rounded-2xl bg-card text-card-foreground p-3 sm:p-4 shadow-2xl border border-border">
                
                <div className="relative aspect-4/3 sm:aspect-1/1 rounded-xl overflow-hidden bg-muted mb-4">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_hero.png-wvc-sizes" alt="Yassine Essaadi - Local SEO Consultant in Lincoln" loading="lazy" decoding="async" className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  
                  <div className="absolute bottom-3 left-3 right-3 bg-card/95 backdrop-blur-md p-3 rounded-lg border border-border shadow-md">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-primary/10 text-primary">
                        <Building2 className="size-5" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground">Yassine Essaadi</p>
                        <p className="text-2xs text-muted-foreground">Founder &amp; Lead SEO Consultant</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2.5">
                  <div className="p-3 rounded-lg bg-muted/60 border border-border/60 flex items-start gap-3">
                    <MapPin className="size-4 text-primary shrink-0 mt-0.5" />
                    <p className="text-xs text-foreground/90 leading-snug">
                      <strong className="font-semibold text-foreground">Lincolnshire Focus:</strong> Tailored strategy designed for businesses targeting Lincoln and UK nationwide growth.
                    </p>
                  </div>

                  <div className="p-3 rounded-lg bg-muted/60 border border-border/60 flex items-start gap-3">
                    <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                    <p className="text-xs text-foreground/90 leading-snug">
                      <strong className="font-semibold text-foreground">100% Direct Work:</strong> Every audit, strategy, and technical fix is executed personally by me.
                    </p>
                  </div>
                </div>

              </div>

              <div className="hidden sm:flex absolute -top-4 -left-4 bg-accent text-accent-foreground text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg items-center gap-1.5">
                <TrendingUp className="size-3.5" />
                <span>Lincoln Map Pack Specialist</span>
              </div>

            </motion.div>
          </div>

        </motion.div>

        <motion.div variants={itemVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="mt-16 pt-12 border-t border-primary-foreground/15 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {localPillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <div key={pillar.id} data-index={i} className="bg-primary-foreground/5 hover:bg-primary-foreground/10 transition-colors p-5 rounded-xl border border-primary-foreground/10 text-left">
                <div className="size-10 rounded-lg bg-accent/20 flex items-center justify-center text-accent mb-4">
                  <IconComponent className="size-5" />
                </div>
                <h3 className="font-serif font-semibold text-base text-primary-foreground mb-2">
                  {pillar.title}
                </h3>
                <p className="text-xs text-primary-foreground/80 leading-relaxed">
                  {pillar.desc}
                </p>
              </div>;
        })}
        </motion.div>

      </div>
    </section>;
}